import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, Trash2, Edit2, X, Ticket } from 'lucide-react';
import {
  fetchCoupons,
  fetchCouponsCount,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../services/api.ts';
import './Coupons.css';

interface FormData {
  code: string;
  description: string;
  discount_type: string;
  discount_value: string;
  min_order_amount: string;
  max_discount_amount: string;
  expires_at: string;
  max_uses: string;
  is_active: boolean;
}

const emptyForm: FormData = {
  code: '',
  description: '',
  discount_type: 'percentage',
  discount_value: '',
  min_order_amount: '',
  max_discount_amount: '',
  expires_at: '',
  max_uses: '',
  is_active: true,
};

const Coupons: React.FC = () => {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const [list, countRes] = await Promise.all([
        fetchCoupons(offset, perPage),
        fetchCouponsCount(),
      ]);
      setCoupons(list);
      setTotal(countRes.count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = coupons.filter((c) => {
    const s = `${c.code} ${c.description || ''}`.toLowerCase();
    return s.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(total / perPage);

  const openAdd = () => {
    setEditingCoupon(null);
    setForm(emptyForm);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (c: any) => {
    setEditingCoupon(c);
    setForm({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      min_order_amount: c.min_order_amount != null ? String(c.min_order_amount) : '',
      max_discount_amount: c.max_discount_amount != null ? String(c.max_discount_amount) : '',
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : '',
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
      is_active: c.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setFormError('Coupon code is required'); return; }
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) { setFormError('Discount value must be positive'); return; }
    if (form.discount_type === 'percentage' && parseFloat(form.discount_value) > 100) { setFormError('Percentage cannot exceed 100%'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const payload: any = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim() || undefined,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : undefined,
        max_discount_amount: form.max_discount_amount ? parseFloat(form.max_discount_amount) : undefined,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : undefined,
      };
      if (editingCoupon) {
        payload.is_active = form.is_active;
        await updateCoupon(editingCoupon.id, payload);
      } else {
        await createCoupon(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await deleteCoupon(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete coupon');
    }
  };

  const formatDiscount = (c: any) => {
    if (c.discount_type === 'percentage') return `${c.discount_value}%`;
    return `$${Number(c.discount_value).toFixed(2)}`;
  };

  const isExpired = (c: any) => c.expires_at && new Date(c.expires_at) < new Date();
  const isLimitReached = (c: any) => c.max_uses != null && c.current_uses >= c.max_uses;

  if (loading) {
    return (
      <div className="coupons-page">
        <div className="coupons-loading"><div className="coupons-spinner" /><p>Loading coupons...</p></div>
      </div>
    );
  }

  return (
    <div className="coupons-page">
      <div className="coupons-header">
        <div className="coupons-header-left">
          <Ticket size={22} />
          <h1>Coupons</h1>
          <span className="coupons-count">{total}</span>
        </div>
        <button className="coupons-btn coupons-btn-primary" onClick={openAdd}>
          <Plus size={16} /> Add Coupon
        </button>
      </div>

      <div className="coupons-search-bar">
        <Search size={18} />
        <input type="text" placeholder="Search by code or description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      {error && <div className="coupons-error">{error}</div>}

      <div className="coupons-table-wrap">
        <table className="coupons-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th>Discount</th>
              <th>Min Order</th>
              <th>Expires</th>
              <th>Uses</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="coupons-empty">No coupons found</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id}>
                <td className="coupons-cell-code">{c.code}</td>
                <td>{c.description || '—'}</td>
                <td>
                  <span className="coupons-badge coupons-badge-info">{formatDiscount(c)}</span>
                  {c.discount_type === 'percentage' && c.max_discount_amount && (
                    <span style={{ marginLeft: 6, fontSize: 12, color: '#888' }}>max ${Number(c.max_discount_amount).toFixed(2)}</span>
                  )}
                </td>
                <td>{c.min_order_amount ? `$${Number(c.min_order_amount).toFixed(2)}` : '—'}</td>
                <td>
                  {c.expires_at ? (
                    <span style={{ color: isExpired(c) ? '#dc2626' : '#333' }}>
                      {new Date(c.expires_at).toLocaleDateString()}
                      {isExpired(c) && <span className="coupons-badge coupons-badge-danger" style={{ marginLeft: 6 }}>Expired</span>}
                    </span>
                  ) : 'No expiry'}
                </td>
                <td>
                  {c.current_uses}{c.max_uses != null ? ` / ${c.max_uses}` : ' / ∞'}
                  {isLimitReached(c) && <span className="coupons-badge coupons-badge-warning" style={{ marginLeft: 6 }}>Full</span>}
                </td>
                <td>
                  {c.is_active && !isExpired(c) && !isLimitReached(c) ? (
                    <span className="coupons-badge coupons-badge-success">Active</span>
                  ) : !c.is_active ? (
                    <span className="coupons-badge coupons-badge-danger">Inactive</span>
                  ) : (
                    <span className="coupons-badge coupons-badge-warning">Unavailable</span>
                  )}
                </td>
                <td>
                  <div className="coupons-cell-actions">
                    <button className="coupons-action-btn coupons-action-edit" title="Edit" onClick={() => openEdit(c)}>
                      <Edit2 size={15} />
                    </button>
                    <button className="coupons-action-btn coupons-action-delete" title="Delete" onClick={() => handleDelete(c.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="coupons-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showModal && (
        <div className="coupons-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="coupons-modal" onClick={e => e.stopPropagation()}>
            <div className="coupons-modal-header">
              <h2>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <button className="coupons-modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="coupons-modal-body">
              {formError && <div className="coupons-form-error">{formError}</div>}
              <div className="coupons-form-row">
                <label>Code *</label>
                <input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="e.g. WELCOME20" style={{ textTransform: 'uppercase', fontFamily: 'monospace' }} />
              </div>
              <div className="coupons-form-row">
                <label>Description</label>
                <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional description" />
              </div>
              <div className="coupons-form-row-inline">
                <div className="coupons-form-row">
                  <label>Discount Type *</label>
                  <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div className="coupons-form-row">
                  <label>Discount Value *</label>
                  <input type="number" min="0" step="0.01" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} placeholder={form.discount_type === 'percentage' ? 'e.g. 10' : 'e.g. 5.00'} />
                </div>
              </div>
              <div className="coupons-form-row-inline">
                <div className="coupons-form-row">
                  <label>Min Order Amount ($)</label>
                  <input type="number" min="0" step="0.01" value={form.min_order_amount} onChange={e => setForm({...form, min_order_amount: e.target.value})} placeholder="No minimum" />
                </div>
                {form.discount_type === 'percentage' && (
                  <div className="coupons-form-row">
                    <label>Max Discount Cap ($)</label>
                    <input type="number" min="0" step="0.01" value={form.max_discount_amount} onChange={e => setForm({...form, max_discount_amount: e.target.value})} placeholder="No cap" />
                  </div>
                )}
              </div>
              <div className="coupons-form-row-inline">
                <div className="coupons-form-row">
                  <label>Expiry Date</label>
                  <input type="datetime-local" value={form.expires_at} onChange={e => setForm({...form, expires_at: e.target.value})} />
                </div>
                <div className="coupons-form-row">
                  <label>Max Uses</label>
                  <input type="number" min="1" step="1" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} placeholder="Unlimited" />
                </div>
              </div>
              {editingCoupon && (
                <div className="coupons-form-row">
                  <label>
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} style={{ marginRight: 8 }} />
                    Active
                  </label>
                </div>
              )}
            </div>
            <div className="coupons-modal-footer">
              <button className="coupons-btn coupons-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="coupons-btn coupons-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;
